import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AtRiskGroup, AtRiskItem, AtRiskReport } from '../../../Api/Projects/projectsApi';
import { Card } from '../../Atoms/Card';

export const VISIBLE_AT_RISK_ITEMS = 2;

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const numberWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

// Known project statuses get bespoke phrasing; anything else falls back to "N in {status}".
const STATUS_PHRASES: Record<string, { singular: string; plural: string }> = {
  Escalated: { singular: 'escalation', plural: 'escalations' },
  'On Hold': { singular: 'stalled on hold', plural: 'stalled on hold' }
};

const groupPhrase = (group: AtRiskGroup): string => {
  const count = group.items.length;
  const phrase = STATUS_PHRASES[group.status];
  const label = phrase ? (count === 1 ? phrase.singular : phrase.plural) : `in ${group.status}`;
  return `${numberWord(count)} ${label}`;
};

export const buildStatusBreakdown = (groups: AtRiskGroup[]): string => {
  const parts = groups.map(groupPhrase);
  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const servicesAtRisk = (count: number): string => `${count} ${count === 1 ? 'Service' : 'Services'} at Risk`;

// Both tiers share the mockup accent; the label ("Delayed" / "Possible Delay") already conveys the tier.
const RISK_BADGE_COLORS: Record<string, string> = {
  yellow: 'text-[#D96E2B]',
  red: 'text-[#D96E2B]'
};

const AtRiskItemRow = ({ item, onClick }: { item: AtRiskItem; onClick: () => void }) => {
  const breadcrumb = [item.productType, item.siteAddress, item.supplier].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-[#F5F5F5]"
      data-at-risk-item
      data-testid={`at-risk-item-${item.assetId}`}
      data-cy={`at-risk-item-${item.assetId}`}>
      {breadcrumb && <div className="text-sm text-grey-10">{breadcrumb}</div>}
      <div className="text-sm mt-1">
        <span className={`${RISK_BADGE_COLORS[item.riskTier] || 'text-grey-7'} font-semibold mr-2`}>
          {item.riskLabel}
        </span>
        <span className="text-grey-9">{item.riskMessage}</span>
      </div>
      {item.nextStep && (
        <div className="text-sm mt-1">
          <span className="text-indigo font-semibold mr-2">Next Step</span>
          <span className="text-grey-9">{item.nextStep}</span>
        </div>
      )}
    </div>
  );
};

export interface AtRiskSectionProps {
  report: AtRiskReport | null;
}

export const AtRiskSection = ({ report }: AtRiskSectionProps) => {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);

  // Item heights vary with next-step text, so the cap is measured off the rendered rows.
  const measureListCap = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const items = list.querySelectorAll<HTMLElement>('[data-at-risk-item]');
    if (items.length <= VISIBLE_AT_RISK_ITEMS) {
      setListMaxHeight(null);
      return;
    }

    const lastVisible = items[VISIBLE_AT_RISK_ITEMS - 1];
    setListMaxHeight(lastVisible.offsetTop + lastVisible.offsetHeight);
  }, []);

  useLayoutEffect(() => {
    measureListCap();
  }, [measureListCap, report]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !contentRef.current) return;

    // Observe the inner content — the capped outer box never re-fires on height-only re-wraps.
    const observer = new ResizeObserver(() => measureListCap());
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [measureListCap, report]);

  if (!report || report.atRiskCount === 0 || report.groups.length === 0) return null;

  const subtitle =
    `${report.atRiskCount} ${report.atRiskCount === 1 ? 'service needs' : 'services need'} attention — ` +
    `${buildStatusBreakdown(report.groups)}. Review the remediation steps below.`;

  return (
    <Card
      useDefaultCardStyles
      data-cy="at-risk-section"
      className="my-6">
      <div className="flex items-center bg-card-1 h-[48px]">
        <div className="flex items-center gap-3 p-5">
          <span
            aria-hidden="true"
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#D96E2B] text-[11px] font-bold leading-none text-white">
            !
          </span>
          <span className="text-[13px] font-semibold text-grey-9">At Risk — Action Needed</span>
        </div>
        <span
          className="ml-auto self-center px-6 text-sm font-medium text-[#D96E2B]"
          data-testid="at-risk-count">
          {servicesAtRisk(report.atRiskCount)}
        </span>
      </div>
      <div
        className="border-t p-5 bg-white rounded-b-lg"
        data-testid="at-risk-section-body">
        <div className="text-sm text-grey-12 mb-4">{subtitle}</div>
        <div
          ref={listRef}
          className={`relative${listMaxHeight === null ? '' : ' overflow-y-auto pr-2'}`}
          style={listMaxHeight === null ? undefined : { maxHeight: listMaxHeight }}
          data-testid="at-risk-list">
          <div
            ref={contentRef}
            className="flex flex-col gap-4">
            {report.groups.map((group) => (
              <div
                key={group.status}
                className="border-l-[3px] border-[#D96E2B] pl-4"
                data-testid={`at-risk-group-${group.status}`}>
                <div className="text-sm font-bold text-grey-9 mb-1">{group.status}</div>
                <div className="flex flex-col gap-3">
                  {group.items.map((item) => (
                    <AtRiskItemRow
                      key={item.assetId}
                      item={item}
                      onClick={() => navigate(`/projects/${encodeURIComponent(item.projectId)}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};
